import { useState, useEffect, useCallback, useMemo } from "react";
import React from 'react';
import {
  useTheme,
  Box,
  Button,
  TextField,
  Typography,
  ButtonGroup,
  IconButton,
  ListItemIcon,
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Paper,
} from "@mui/material";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { LocalizationProvider, DatePicker, DateField } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { ThreeDots } from "react-loader-spinner";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { API_BASE_URL } from "../../config";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { de } from 'date-fns/locale';
import { format, addDays } from 'date-fns';
import TitleHeader from "../../components/TitleHeader";
import Loading from "../../components/Loading";
import BreadcrumbsNav from "../../components/BreadcrumbsNav";
import useToast from "../../components/useToast";

const predefinedColors = [
  "#FFEBEE", // Light red
  "#E3F2FD", // Light blue
  "#E8F5E9", // Light green
  "#FFF3E0", // Light orange
  "#F3E5F5", // Light purple
  "#FFCDD2", // Another light red variant
  "#C8E6C9", // Another light green variant
];

const TimeReqTemplate = ({ timereq }) => {
  const theme = useTheme();
  const [timereqData, setTimeReqData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [openingHours, setOpeningHours] = useState([]);
  const [closingHours, setClosingHours] = useState([]);
  const [closingDays, setClosingDays] = useState([]);
  const [startBreak, setStartBreak] = useState([]);
  const [endBreak, setEndBreak] = useState([]);
  const [weekAdjustment, setWeekAdjustment] = useState(0);
  const [slotDuration, setSlotDuration] = useState(0);
  const token = localStorage.getItem("session_token");
  const [employeeCount, setEmployeeCount] = useState({});
  const [selectedButtons, setSelectedButtons] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [slotEmployeeCounts, setSlotEmployeeCounts] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [shiftPlanning, setShiftPlanning] = useState("");
  const [shiftColors, setShiftColors] = useState({});
  const [shiftList, setShiftList] = useState([]);
  const [shiftValues, setShiftValues] = useState({});
  const navigate = useNavigate();
  const [department_list, setDepartmentList] = useState([]);
  const [template_list, setTemplateList] = useState([]);
  const { t, i18n } = useTranslation();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [templateNameList, setTemplateNameList] = useState([]);
  const[templateName, setTemplateName] = useState("");
  const[newTemplateName, setNewTemplateName] = useState("");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const { success, error, info, loading, close } = useToast();

  const themeColors = tokens(theme.palette.mode);

  const getGreenShade = (employeeCount) => {
    const maxIndex = themeColors.timereq.length - 1;
    const index = Math.min(employeeCount, maxIndex);
    return themeColors.timereq[index];
  };


  const convertTimeToMinutes = (timeStr) => {
    if (!timeStr) return undefined;
    const [hour, minute] = timeStr.split(":");
    return parseInt(hour) * 60 + parseInt(minute);
  };

  const formatTimeFromMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Helper function to check if the current time is within the operating hours and break time
  const isTimeWithinRange = (
    current,
    opening,
    startBreak,
    endBreak,
    closing
  ) => {
    if (current === undefined || current === null) {
      return false;
    } else {
      if (closing < opening || startBreak < opening) {
        if (closing === null || closing === undefined || isNaN(closing)) {
          return current >= opening || current < startBreak;
        } else {
          return (
            (current >= opening && current < startBreak) ||
            (current >= endBreak && current <= 1440) ||
            current < closing
          );
        }
      } else {
        if (closing === null || closing === undefined) {
          return current >= opening && current <= startBreak;
        }
        return (
          (current >= opening && current < startBreak) ||
          (current >= endBreak && current <= closing)
        );
      }
    }
  };

  /// Define Click and Drag Function
  useEffect(() => {
    const endDrag = () => setIsDragging(false);
    window.addEventListener("mouseup", endDrag);

    return () => {
      window.removeEventListener("mouseup", endDrag);
    };
  }, []);

  const handleMouseDown = useCallback((colIndex, btnIndex) => {
    setIsDragging(true);
    toggleButtonSelection(colIndex, btnIndex);
  }, []);

  const toggleButtonSelection = (columnIndex, btnIndex) => {
    const selectedKey = `${columnIndex}-${btnIndex}`;
    setSelectedButtons((prevSelectedButtons) => {
      if (prevSelectedButtons.includes(selectedKey)) {
        return prevSelectedButtons.filter((key) => key !== selectedKey);
      } else {
        return [...prevSelectedButtons, selectedKey];
      }
    });
  };

  const setEmployees = (e, columnIndex) => {
    const value = parseInt(e.target.value);
    setEmployeeCount(value);
  };

  const EnteredSlots = (columnIndex) => {
    // Clone the current state
    const updatedCounts = { ...slotEmployeeCounts };
  
    // If employeeCount is not set or is NaN (empty input), set it to 0
    const count = isNaN(employeeCount) ? 0 : employeeCount;
  
    // Update only the slots that are selected for this columnIndex
    selectedButtons.forEach((slot) => {
      updatedCounts[slot] = count;
    });
  
    // Update state
    setSlotEmployeeCounts(updatedCounts);
    setSelectedButtons([]);
  
    // Create a new object for employeeCount while keeping all the old values
    const newEmployeeCount = { ...employeeCount };
  
    // Update only the value for the given columnIndex
    newEmployeeCount[columnIndex] = undefined;
  
    // Update state
    setEmployeeCount(newEmployeeCount);
  };


  useEffect(() => {
    const fetchTimeReqData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/requirement/workforce`,
          {
            params: {
              week_adjustment: weekAdjustment,
              selectedDepartment: selectedDepartment,
              // don't send "new_template" to backend
              selectedTemplate: selectedTemplate === "new_template" ? "" : selectedTemplate,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        const data = response.data;
        setTimeReqData(data);
        setDepartmentList(data.department_list);
        setTemplateList(data.template_list);
        setShiftList(data.shift_list);

        // colors
        const colors = {};
        data.shift_list.forEach((shift, index) => {
          const baseColor = predefinedColors[index % predefinedColors.length];
          colors[shift] = { base: baseColor };
        });
        setShiftColors(colors);

        setSlotDuration(data.minutes);
        setTemplateNameList(data.timereq_template_names);
        setShiftPlanning(data.shift_planning);

        // 🔸 NEW: decide source based on template selection + mode
        const hasExistingTemplate =
          selectedTemplate &&
          selectedTemplate !== "new_template" &&
          data.template_dict;

        if (data.shift_planning === 0) {
          // slot-based templates
          const baseCounts = hasExistingTemplate
            ? (data.template_dict || {})
            : (data.timereq_dict || {});
          setSlotEmployeeCounts(baseCounts);
          setShiftValues({});
        } else {
          // shift-based templates
          const baseShifts = hasExistingTemplate
            ? (data.template_dict || {})
            : (data.shift_data || {});
          setShiftValues(baseShifts);
          setSlotEmployeeCounts({});
        }

        const openingHours = [];
        const startBreak = [];
        const endBreak = [];
        const closingHours = [];

        for (let i = 0; i < data.day_num; i++) {
          openingHours.push(data.opening_dict[`${i + 1}&0`]);
          startBreak.push(data.opening_dict[`${i + 1}&1`]);
          endBreak.push(data.opening_dict[`${i + 1}&2`]);
          closingHours.push(data.opening_dict[`${i + 1}&3`]);
        }

        setOpeningHours(openingHours);
        setStartBreak(startBreak);
        setEndBreak(endBreak);
        setClosingHours(closingHours);
        setClosingDays(response.data.closing_days);
        setIsLoading(false);
      } catch (error) {
        // console.error("Error fetching Time Requirements:", error);
        setIsLoading(false);
        if (error.response.status === 401 || error.response.status === 500) {
          navigate("/login");
        }
      }
    };

    fetchTimeReqData();
  }, [weekAdjustment, token, selectedDepartment, selectedTemplate]);

  useEffect(() => {
    // Check if there's exactly one department in the list
    if (department_list.length === 1) {
      setSelectedDepartment(department_list[0]);
    }
  }, [department_list]);


  const handleFormSubmit = async (buttonName) => {
    if (templateName === "" && selectedTemplate === "new_template") {
      error(t("planning.error_select_template"));
      return; 
    }
    let missingEntries = false;
    Object.entries(slotEmployeeCounts).forEach(([_, count]) => {
      if (count === undefined || count === '') { // Assuming empty string or undefined as missing
        missingEntries = true;
      }
    });

    setIsLoading(true);

    try {
      // console.log(slotEmployeeCounts);
      const payload = {};
      Object.entries(slotEmployeeCounts).forEach(([key, count]) => {
        if (count !== undefined) {
          // Check if count is not undefined
          const [columnIndex, btnIndex] = key.split("-");
          const dayNum = columnIndex; // Assuming columnIndex starts from 0
          const currentTime = timereqData.slots_dict[parseInt(btnIndex)];
          const newKey = `worker_${dayNum}_${currentTime}`;
          payload[newKey] = count.toString();
        }
      });
      payload["button"] = buttonName;
      payload["selected_name"] = selectedTemplate;
      payload["template_name"] = templateName;
      payload["new_template_name"] = newTemplateName;
      payload["department"] = selectedDepartment;
      payload["shifts"] = shiftValues;
      // Send the updated form values to the server for database update
      // console.log("Final payload before sending to server:", payload);
      await axios.post(
        `${API_BASE_URL}/api/requirement/workforce?week_adjustment=${weekAdjustment}&selectedDepartment=${encodeURIComponent(
          selectedDepartment
        )}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setSelectedTemplate("");
      setTemplateName("");
      setNewTemplateName("");
      success(t("notification.successful"));
      setIsLoading(false);
      // console.log("Sending this data to server:", payload);
    } catch (error) {
      error(t("notification.error"));
      setIsLoading(false)
    }
  };

  const handleShiftChange = (index, value) => {
    const newValue = (value === "" || value === "0") ? 0 : value;
  
    setShiftValues((prev) => ({ ...prev, [index]: newValue }));
  };


  const DeletionDialog = (
    <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
      <DialogContent>
        {t('planning.deletion_confirmation')}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenConfirmDialog(false)}>{t("cancel")}</Button>
        <Button onClick={() => {
          setOpenConfirmDialog(false);
          handleFormSubmit("Delete Template");
        }} color="primary">{t("confirm")}</Button>
      </DialogActions>
    </Dialog>
  );


  return (
    <Box m={2}>
      <Loading open={isLoading} />
      <BreadcrumbsNav
        items={[
          { label: t("planning.title") || "Availability", to: "/planning" },
          { label: t("planning.create_template") || "Availability Template" },
        ]}
      />

      {DeletionDialog}

      <TitleHeader
        title={t("planning.create_template")}
        subtitle={t("planning.create_template_subtitle")}
      />
      <Paper
        elevation={5}
        sx={{
          p: 2,
          bgcolor: "background.box",
          //border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            maxHeight: 'calc(100vh - 100px)', 
            overflowY: 'auto',
            p: 2,
            bgcolor: "background.box",
            borderRadius: 1,
          }}
        >

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              width: "100%",
              marginBottom: "1rem",
              background: "white",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
            }}
          >
            {/* Left-aligned items */}
            <Box 
              sx={{ 
                position: 'absolute', 
                left: 0, display: 'flex', 
                alignItems: 'center',
                marginLeft: "10px",
                marginTop: "10px",
                marginBottom: "10px", 
              }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="template-select-label">{t("planning.select_template")}</InputLabel>
                <Select
                  type="text"
                  label={t("planning.select_template")}
                  name="template_name"
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  renderValue={(selected) => {
                    if (selected === "new_template") {
                        return t("planning.new_template");
                    }
                    return selected;
                  }}
                  sx={{
                      color: "black",
                      backgroundColor: 'white',
                      marginRight: "0.2rem",
                      height: "35px", // explicitly set height
                      ".MuiInputBase-root": {
                          height: "35px", // explicitly set input field height
                          fontSize: "10px", // explicitly set font size
                          backgroundColor: 'white',
                      },
                      ".MuiSelect-icon": {
                          // Change color of icon to black
                          color: "black",
                      },
                  }}
                >
                  <MenuItem value="new_template">
                    <ListItemIcon>
                      <AddIcon fontSize="small" sx={{ color: "#22E3B6" }} />
                    </ListItemIcon>
                    <Typography sx={{ color: "#22E3B6" }}>{t("planning.new_template")}</Typography>
                  </MenuItem>

                  {templateNameList.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedTemplate==="new_template" && (
              <TextField
                placeholder={t("planning.template_name")}
                variant="outlined"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                fullWidth
                InputProps={{  
                  style: {
                    height: 35, 
                  }
                }}
                inputProps={{ 
                  min: 0,
                  style: {
                    padding: '10px 14px',  
                    backgroundColor: 'white',
                  }
                }}
              />
              )}
              {selectedTemplate!=="" && selectedTemplate!=="new_template" && (
              <TextField
                placeholder={t("planning.template_name_change")}
                variant="outlined"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                fullWidth
                InputProps={{  
                  style: {
                    height: 35, 
                  }
                }}
                inputProps={{ 
                  min: 0,
                  style: {
                    padding: '10px 14px',  
                    backgroundColor: 'white',
                  }
                }}
              />
              )}
              {selectedTemplate!=="" && selectedTemplate!=="new_template" && (
              <IconButton
                  onClick={() => setOpenConfirmDialog(true)}
                  sx={{ ml: 'auto' }}
                >
                  <DeleteIcon sx={{ color: 'red' }} />
                </IconButton>
              )}
            </Box>

            {/* Centered item */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                marginTop: "25px",
                marginBottom: "25px", 
              }}></Box>
            {/* Right-aligned items */}
            <Box 
              sx={{ 
                position: 'absolute', 
                right: 0, 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                marginRight: "10px",
                marginTop: "10px",
                marginBottom: "10px", 
              }}>
              <Button
                onClick={() => handleFormSubmit("Save Template")}
                variant="contained" // Changed to contained
              >
                {t("planning.savetemplate")}
              </Button>
            </Box>
                          
          </Box>

          {(selectedTemplate && shiftPlanning === 0) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "30%",
              marginBottom: "1rem",
              background: "white",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: "10px" }}>
              <TextField
                  type="number"
                  value={employeeCount || ""}
                  onChange={(e) => setEmployees(e)}
                  placeholder={t("planning.enteremployeecount")}
                  variant="outlined"
                  fullWidth
                  InputProps={{  // Note the capital 'I' which styles the TextField wrapper
                    style: {
                      height: 35,  // Adjust height to match the button
                    }
                  }}
                  inputProps={{  // Lowercase 'i' affects the <input> element itself
                    min: 0,
                    style: {
                      padding: '10px 14px',  // Adjust padding to center the text vertically
                      backgroundColor: 'white',
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={EnteredSlots}
                  sx={{
                    height: 35,
                    marginBottom: "10px",
                    marginTop: "10px",
                    marginLeft: "10px"
                  }}
                >
                  {t("button.save_2")}
                </Button>

            </Box>                      
          </Box>
          )}
      
          <span></span>

          {(selectedTemplate && shiftPlanning === 0) && (
          <Box
            sx={{
              maxHeight: 'calc(75vh - 75px)',
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)", // 7 columns for each day
              gap: theme.spacing(1), // Reduce the gap if it's too large
              marginBottom: "1rem",
              background: "white",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
              "& > :first-of-type": { // Apply styles to the first child only
                marginLeft: "10px",
              },
              "& > :last-of-type": { // Apply styles to the last child only
                marginRight: "10px",
              }
            }}
        >
          {Array.from({ length: timereqData.day_num }).map((_, columnIndex) => {
            // Berechnen des Datums für jeden Tag basierend auf dem Startdatum und columnIndex

            return (
              <Box
                key={`column-${columnIndex}`}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginTop: "5px",
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  component="div"
                  sx={{
                  fontWeight: 'bold',  // Makes the text bold
                  textDecoration: 'underline'  // Underlines the text
                }}>
                  {timereqData.weekdays[columnIndex]}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%", // Full width
                    maxHeight: 'calc(55vh - 55px)',
                    overflowY: "auto", // To allow scrolling if the content exceeds the fixed height
                    marginTop: "20px",
                    marginBottom: "10px",
                    background: "white"
                  }}
                >
                  {Array.from({ length: timereqData.daily_slots }).map(
                    (_, btnIndex) => {
                      const timereqKey = `${columnIndex}-${btnIndex}`;
                      const currentTimeMinutes = convertTimeToMinutes(
                        timereqData.slots_dict[btnIndex]
                      );
                      const openingTimeMinutes = convertTimeToMinutes(
                        openingHours[columnIndex]
                      );
                      const startBreakTimeMinutes =
                        convertTimeToMinutes(startBreak[columnIndex]) - 1;
                      const endBreakTimeMinutes = convertTimeToMinutes(
                        endBreak[columnIndex]
                      );
                      const closingTimeMinutes =
                        convertTimeToMinutes(closingHours[columnIndex]) - 1;

                      const startTimeMinutes = convertTimeToMinutes(timereqData.slots_dict[btnIndex]);
                      const endTimeMinutes = startTimeMinutes + slotDuration;
                      const startTimeFormatted = formatTimeFromMinutes(startTimeMinutes);
                      const endTimeFormatted = formatTimeFromMinutes(endTimeMinutes);

                      if (
                        isTimeWithinRange(
                          currentTimeMinutes,
                          openingTimeMinutes,
                          startBreakTimeMinutes,
                          endBreakTimeMinutes,
                          closingTimeMinutes
                        )
                      ) {
                        const isSelected = selectedButtons.includes(
                          `${columnIndex}-${btnIndex}`
                        );
                        const employeeCountForThisSlot =
                          slotEmployeeCounts[`${columnIndex}-${btnIndex}`] || "";

                          const buttonStyle = {
                            variant: "outlined",
                            color: "secondary",
                            style: {
                              width: "100%",
                              borderColor: "lightgrey",
                              "&:hover": {
                                borderColor: "white",
                              }
                            }
                          };

                        if (isSelected) {
                          buttonStyle.variant = "contained";
                          buttonStyle.color = "secondary";
                        } else if (employeeCountForThisSlot > 0) {
                          buttonStyle.variant = "contained";
                          buttonStyle.style.backgroundColor = getGreenShade(employeeCountForThisSlot);
                          buttonStyle.style.color = "white";
                        }

                        return (
                          <Button
                            key={`btn-${columnIndex}-${btnIndex}`}
                            {...buttonStyle}
                            onMouseDown={() => handleMouseDown(columnIndex, btnIndex)}
                            onMouseOver={() => {
                              if (isDragging) {
                                toggleButtonSelection(columnIndex, btnIndex);
                              }
                            }}
                            onMouseUp={() => setIsDragging(false)}
                          >
                            {`${startTimeFormatted} - ${endTimeFormatted}`}
                            {employeeCountForThisSlot && (
                              <>
                                <span style={{ margin: '0 10px' }}>|</span> 
                                <span style={{ marginLeft: '10px' }}>{employeeCountForThisSlot}</span>
                              </>
                            )}
                          </Button>
                        );
                      }
                      return null; // Return null if the current time is outside the opening and closing hours
                    }
                  )}
                </Box>
              </Box>
            );
          })}
          </Box>
          )}



          {(selectedTemplate  && shiftPlanning === 1) && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr repeat(7, 1fr)", // 1 column for shift name + 7 columns for weekdays
                gap: theme.spacing(1),
                padding: "10px",
                background: "white",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
                overflow: 'auto',
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "left",
                  padding: "10px",
                  marginLeft: "5px",
                  marginRight: "5px",
                  marginTop: "5px",
                  fontWeight: "bold",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {t("planning.shift_name")}
                </Typography>
              </Box>
              {Array.from({ length: timereqData.day_num }).map((_, columnIndex) => {
                const dateForDay = addDays(new Date(timereqData.week_start), columnIndex);
                return (
                  <Box
                    key={`header-column-${columnIndex}`}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      padding: "10px",
                      marginLeft: "5px",
                      marginRight: "5px",
                      marginTop: "5px",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {format(dateForDay, "EEEE", { locale: de })}
                    </Typography>
                  </Box>
                );
              })}

              {shiftList.map((shift, shiftIndex) => (
                <React.Fragment key={`shift-row-${shiftIndex}`}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "left",
                      padding: "10px",
                      marginLeft: "5px",
                      marginRight: "5px",
                      marginTop: "5px",
                      fontWeight: "bold",
                      backgroundColor: shiftColors[shift]?.base || "white",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                  {shiftPlanning === 0 && (
                    <>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {shift[0]}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontStyle: "italic", color: "gray" }}>
                      {shift[1]}
                    </Typography>
                    {shift[2] !== 'None' &&(
                      <Typography variant="subtitle2" sx={{ fontStyle: "italic", color: "gray" }}>
                        {shift[2]}
                      </Typography>
                    )
                    }
                    </>
                  )}
                  {shiftPlanning === 1 && (
                    <>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {shift[1]}
                    </Typography>
                    {shift[2] !== 'None' &&(
                      <Typography variant="subtitle2" sx={{ fontStyle: "italic", color: "gray" }}>
                        {shift[2]}
                      </Typography>
                    )
                    }
                    </>
                  )}
                  </Box>

                  {Array.from({ length: timereqData.day_num }).map((_, columnIndex) => {
                    const isClosed = closingDays[columnIndex] === 0;
                    return(
                      <Box
                        key={`shift-${shiftIndex}-day-${columnIndex}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "5px",
                          marginLeft: "5px",
                          marginRight: "5px",
                          marginTop: "5px",
                          borderBottom: "1px solid #e0e0e0",
                          backgroundColor: shiftColors[shift]?.base || "white",
                        }}
                      >
                      {isClosed ? (
                        <Box
                          sx={{
                            width: "100%",
                            backgroundColor: "#f5f5f5",
                            padding: "10px",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ color: "black" }}>
                          {t("availabilty.closed")}
                          </Typography>
                        </Box>
                      ) : (
                      <TextField
                        fullWidth
                        variant="filled"
                        type="number"
                        placeholder={t("planning.shift_label")}
                        value={shiftValues[`${shift[1]}_${columnIndex}`] || ""}
                        onChange={(e) =>
                          handleShiftChange(`${shift[1]}_${columnIndex}`, e.target.value)
                        }
                        InputProps={{
                          style: {
                            backgroundColor: shiftColors[shift]?.lighter || "white",
                          },
                        }}
                        sx={{
                          height: 'auto',
                          "& .MuiFilledInput-input": {
                            padding: "10px",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                          },
                        }}
                      />
                      )}
                    </Box>
                    );
                  })}
                </React.Fragment>
              ))}
          </Box>
          )}      
        </Box>
      </Paper>
    </Box>
  
  );
};

export default TimeReqTemplate;



